import React, { PropsWithChildren, useContext } from 'react';
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { isSSR } from 'Utilities/helpers';
import { safeLocalStorage } from 'Utilities/storages';

import { APIResponseStatus } from 'Services/api.types';

import { ComponentContent, ComponentsWrapper, CONTENT_SIZES } from 'Layout';

import PageLoader from '../../../../components/transitions/PageLoader/PageLoader';
import { BASE_ROUTE as HANDSHAKE_BASE_ROUTE } from '../../postSale/handshake/Handshake.helpers';
import { VehiclePaymentDetails } from '../../postSale/payments/VehiclePaymentDetails/VehiclePaymentDetails';
import { useIsDealerDirectHandshake } from '../../vehicleCollection/hooks/useIsDealerDirectHandshake';
import { usePaymentDetailsSub } from '../../vehicleCollection/hooks/usePaymentDetailsSub';
import { useVehicleCollectionSub } from '../../vehicleCollection/hooks/useVehicleCollectionSub';
import { AddBankDetails } from '../addBankDetails/AddBankDetails';
import DealerDetails from '../DealerDetails/DealerDetails';
import {
  DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY, DOCUMENT_CAPTURE_RESPONSE_API_KEYS,
  DOCUMENT_CAPTURE_REVIEW_COMPLETE_ALLOWED_STATES, DOCUMENT_CAPTURE_REVIEW_KEY,
  DOCUMENT_CAPTURE_ROUTE_NAMES, DOCUMENTS_COMPLETED_ALLOWED_STATUSES,
  PAYMENT_STATUS_ROUTE_NAMES,
} from '../DocumentCapture.helpers';
import { useDocumentCaptureRoutesState } from '../DocumentCapture.hooks';
import { DocumentCaptureReviewCompleteAllowedStatesValue } from '../DocumentCapture.types';
import { DocumentCaptureConfirmationProgressContext, DocumentCaptureOfferContext } from '../DocumentCaptureContexts';
import { OfferSummaryWithContext } from '../OfferSummary/OfferSummary';
import { ReviewStatusView } from '../ReviewStatusView/ReviewStatusView';
import { SummaryOfSale } from '../SummaryOfSale/SummaryOfSale';
import { IncodeSDKPageHOC } from '../uploadDocuments/pages/IncodeSDKPage/IncodeSDKPageHOC';
import UploadDocuments from '../uploadDocuments/UploadDocuments';
import { useDocumentCaptureExperimentDecisions } from '../useDocumentCaptureExperimentDecisions';

import styles from './DocumentCaptureView.scss';

export const DOCUMENT_CAPTURE_MAIN_WIZARD_STORAGE_KEY = 'documentCaptureMainWizardState';

type Slug = typeof DOCUMENTS_COMPLETED_ALLOWED_STATUSES[keyof typeof DOCUMENTS_COMPLETED_ALLOWED_STATUSES]

type DocumentCaptureViewProps = {
  status: APIResponseStatus;
}

export const DocumentCaptureViewLayout: React.FC<PropsWithChildren> = ({ children }) => (
  <ComponentsWrapper className={styles.documentCaptureMainWrapper} selector="main">
    <ComponentContent
      className={styles.documentCaptureViewMainWrapper}
      data-testid="documentCaptureWrapper"
      maxWidth={CONTENT_SIZES.container}
    >
      { children }
    </ComponentContent>
  </ComponentsWrapper>
);

const DocumentCaptureView = ({ status }: DocumentCaptureViewProps) => {
  const documentCaptureConfirmationProgressContext = useContext(DocumentCaptureConfirmationProgressContext);
  const { isMandatoryBankDetailsEligible } = useDocumentCaptureExperimentDecisions();

  const {
    isNESettled: shouldRedirectToFundsReceivedPage,
    isPostSaleDataLoading,
    isSaleComplete,
  } = useVehicleCollectionSub();

  const { paymentPageStatus, shouldRedirectToVehiclePaymentPage } = usePaymentDetailsSub();

  const shouldRedirectToSaleCompletePage = isSaleComplete || paymentPageStatus === 'success';

  const {
    handshakeDealer,
    isDealerDirect: shouldRedirectToDealerDetails,
    isHandshakePending,
  } = useIsDealerDirectHandshake();

  const { enquiryId, state = {} } = useContext(DocumentCaptureOfferContext) || {};
  const { slug } = state;
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);
  const { params } = useRouteMatch<{ vrm: string }>();
  const { pathname } = useLocation();

  const showReviewStatus = !isSSR() ? (safeLocalStorage.getItem(DOCUMENT_CAPTURE_REVIEW_KEY) || []) : [];
  const showAutomatedReviewStatus = !isSSR()
    ? (safeLocalStorage.getItem(DOCUMENT_CAPTURE_AUTOMATED_REVIEW_KEY) || [])
    : [];

  // Due to a premature render from routing, the previous vehicle information (status and enquiryId)
  // are still in memory before PageLoader can be rendered (as status is always SUCCESS and never LOADING).
  // This in 99% of cases is fine, but in the edge case where a seller has 2 cars that have been sold but
  // only one has had their review status checked, it means if you switch between the 2 via the header links
  // both will end up viewing the Review Status page.
  // So here we check to see if the vehicle is the same as what we have in the URL, if not, then rerender the
  // page (via PageLoader) for the correct information.
  const currentVehicleMatchUrl = params?.vrm === vehicle?.vrm;

  const {
    [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.BANK_DETAILS_ADDED]: bankDetailsAdded,
    [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.CONFIRMED_SALE]: saleConfirmed,
    [DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS_FULFILLED]: documentsAdded,
  } = useDocumentCaptureRoutesState(documentCaptureConfirmationProgressContext.progress);

  if (status === APIResponseStatus.LOADING || !currentVehicleMatchUrl || isPostSaleDataLoading || isHandshakePending) {
    return (<PageLoader />);
  }

  const ADD_BANK_DETAILS_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.ADD_BANK_DETAILS}`;
  const CONFIRM_SALE_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.CONFIRM_SALE}`;
  const UPLOAD_DOCUMENTS_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_DOCUMENTS}/:kind?`;
  const CONFIRM_SALE_SUMMARY_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.CONFIRM_SALE_SUMMARY}`;
  const AUTOMATED_DOCS_PAGE_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.AUTOMATED_DOCS_PAGE}/:step?`;
  const REVIEW_STATUS_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.REVIEW_STATUS}`;
  const DEALER_DETAILS_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.DEALER_DETAILS}`;
  const VEHICLE_COLLECTION_PATH = `/${params.vrm}/${DOCUMENT_CAPTURE_ROUTE_NAMES.VEHICLE_COLLECTION}`;
  const HANDSHAKE_PATH = `/${params.vrm}${HANDSHAKE_BASE_ROUTE}`;

  /**
   * Routes below are for post sale experience, but they rely on components and routing
   * of Document Capture, hence they are defined here.
   */
  const FINANCE_FUNDS_RECEIVED_PATH = `/${params.vrm}/${PAYMENT_STATUS_ROUTE_NAMES.FINANCE_FUNDS_RECEIVED}`;
  const SALE_COMPLETE_PATH = `/${params.vrm}/${PAYMENT_STATUS_ROUTE_NAMES.SALE_COMPLETE}`;
  const VEHICLE_PAYMENT_PATH = `/${params.vrm}/${PAYMENT_STATUS_ROUTE_NAMES.VEHICLE_PAYMENT}`;

  /**
   * The dealer details page and beyond is using the new layout, and since global
   * Document Capture layout is defined HERE, not in components, we have to opt out them using routes.
   */
  const OPT_OUT_LAYOUT_PAGES = new Set([
    DEALER_DETAILS_PATH,
    FINANCE_FUNDS_RECEIVED_PATH,
    SALE_COMPLETE_PATH,
    VEHICLE_PAYMENT_PATH,
  ]);

  const shouldOptOutOfLayout = OPT_OUT_LAYOUT_PAGES.has(pathname);

  const CONFIRM_SALE_ROUTE: React.ReactElement = (
    <Route
      key={DOCUMENT_CAPTURE_ROUTE_NAMES.CONFIRM_SALE}
      exact
      component={OfferSummaryWithContext}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={DOCUMENT_CAPTURE_ROUTE_NAMES.CONFIRM_SALE}
      path={CONFIRM_SALE_PATH}
    />
  );

  const ADD_BANK_DETAILS_ROUTE: React.ReactElement = (
    <Route
      key={DOCUMENT_CAPTURE_ROUTE_NAMES.ADD_BANK_DETAILS}
      exact
      component={AddBankDetails}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={DOCUMENT_CAPTURE_ROUTE_NAMES.ADD_BANK_DETAILS}
      path={ADD_BANK_DETAILS_PATH}
    />
  );

  const UPLOAD_DOCUMENTS_ROUTE: React.ReactElement = (
    <Route
      key={DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_DOCUMENTS}
      exact
      component={UploadDocuments}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_DOCUMENTS}
      path={UPLOAD_DOCUMENTS_PATH}
    />
  );

  const SUMMARY_ROUTE: React.ReactElement = (
    <Route
      key={DOCUMENT_CAPTURE_ROUTE_NAMES.CONFIRM_SALE_SUMMARY}
      exact
      component={SummaryOfSale}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={DOCUMENT_CAPTURE_ROUTE_NAMES.CONFIRM_SALE_SUMMARY}
      path={CONFIRM_SALE_SUMMARY_PATH}
    />
  );

  const INCODE_SDK_ROUTE: React.ReactElement = (
    <Route
      key={DOCUMENT_CAPTURE_ROUTE_NAMES.AUTOMATED_DOCS_PAGE}
      exact
      component={IncodeSDKPageHOC}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={DOCUMENT_CAPTURE_ROUTE_NAMES.AUTOMATED_DOCS_PAGE}
      path={AUTOMATED_DOCS_PAGE_PATH}
    />
  );

  const REVIEW_STATUS_ROUTE: React.ReactElement = (
    <Route
      key={DOCUMENT_CAPTURE_ROUTE_NAMES.REVIEW_STATUS}
      exact
      component={ReviewStatusView}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={DOCUMENT_CAPTURE_ROUTE_NAMES.REVIEW_STATUS}
      path={REVIEW_STATUS_PATH}
    />
  );

  const DEALER_DETAILS_ROUTE: React.ReactElement = (
    <Route
      key={DOCUMENT_CAPTURE_ROUTE_NAMES.DEALER_DETAILS}
      exact
      component={DealerDetails}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={DOCUMENT_CAPTURE_ROUTE_NAMES.DEALER_DETAILS}
      path={DEALER_DETAILS_PATH}
    />
  );

  const FINANCE_FUNDS_RECEIVED_ROUTE: React.ReactElement = (
    <Route
      key={PAYMENT_STATUS_ROUTE_NAMES.FINANCE_FUNDS_RECEIVED}
      exact
      component={DealerDetails}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={PAYMENT_STATUS_ROUTE_NAMES.FINANCE_FUNDS_RECEIVED}
      path={FINANCE_FUNDS_RECEIVED_PATH}
    />
  );

  const SALE_COMPLETE_ROUTE: React.ReactElement = (
    <Route
      key={PAYMENT_STATUS_ROUTE_NAMES.SALE_COMPLETE}
      exact
      component={DealerDetails}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={PAYMENT_STATUS_ROUTE_NAMES.SALE_COMPLETE}
      path={SALE_COMPLETE_PATH}
    />
  );

  const VEHICLE_PAYMENT_ROUTE: React.ReactElement = (
    <Route
      key={PAYMENT_STATUS_ROUTE_NAMES.VEHICLE_PAYMENT}
      exact
      component={VehiclePaymentDetails}
      // @ts-expect-error Name not a valid prop but used for navigation
      name={PAYMENT_STATUS_ROUTE_NAMES.VEHICLE_PAYMENT}
      path={VEHICLE_PAYMENT_PATH}
    />
  );

  // Router
  const resolveRoutes = () => {
    const routes = [] as React.ReactElement[];

    const isDocCaptureCompleted = Boolean(
      Object.values(DOCUMENTS_COMPLETED_ALLOWED_STATUSES).includes(slug as Slug)
        || (saleConfirmed && bankDetailsAdded && documentsAdded),
    );

    const canShowDealerDetailsRoute = DOCUMENT_CAPTURE_REVIEW_COMPLETE_ALLOWED_STATES.includes(
      slug as DocumentCaptureReviewCompleteAllowedStatesValue,
    );

    const canShowReviewStatusView = showReviewStatus.includes((enquiryId as number).toString());
    const isAutomatedReviewStatusViewActive = showAutomatedReviewStatus
      .includes((enquiryId as number).toString());

    // If user didn't confirm sale we only want to enable him to enter confirm sale page
    if (!saleConfirmed) {
      return routes.concat(
        CONFIRM_SALE_ROUTE,
        // Capture any other direct routes entry such as upload documents and redirect to confirm sale
        <Redirect key="redirect" to={CONFIRM_SALE_PATH} />,
      );
    }

    // If user has completed steps and has continued to documents reviewed status, redirect to sale status page
    if (isDocCaptureCompleted && canShowDealerDetailsRoute) {
      // If sale is complete, only allow sale complete page
      if (shouldRedirectToSaleCompletePage) {
        return routes.concat(
          SALE_COMPLETE_ROUTE,
          <Redirect key="redirect" to={SALE_COMPLETE_PATH} />,
        );
      }

      // If there is payment to approve, redirect seller to payment approval
      if (shouldRedirectToVehiclePaymentPage) {
        return routes.concat(
          VEHICLE_PAYMENT_ROUTE,
          <Redirect key="redirect" to={VEHICLE_PAYMENT_PATH} />,
        );
      }

      // If finance payments funds received redirect to finance funds received page
      if (shouldRedirectToFundsReceivedPage) {
        return routes.concat(
          FINANCE_FUNDS_RECEIVED_ROUTE,
          <Redirect key="redirect" to={FINANCE_FUNDS_RECEIVED_PATH} />,
        );
      }

      if (shouldRedirectToDealerDetails) {
        if (handshakeDealer) {
          return (<Redirect key="redirect" to={HANDSHAKE_PATH} />);
        }

        return routes.concat(
          DEALER_DETAILS_ROUTE,
          <Redirect key="redirect" to={DEALER_DETAILS_PATH} />,
        );
      }

      // Else, render collection path
      return routes.concat(
        <Redirect key="redirect" to={VEHICLE_COLLECTION_PATH} />,
      );
    }

    // If user has completed steps and has continued to review their document status, redirect to review status page
    if (isDocCaptureCompleted && canShowReviewStatusView) {
      return routes.concat(
        REVIEW_STATUS_ROUTE,
        <Redirect key="redirect" to={REVIEW_STATUS_PATH} />,
      );
    }

    // If the user hasn't added bank details, not in negative equity and has not added any documents yet,
    // redirect them to add bank details before continuing
    if (isMandatoryBankDetailsEligible && !bankDetailsAdded) {
      return routes.concat(
        ADD_BANK_DETAILS_ROUTE,
        <Redirect key="redirect" to={ADD_BANK_DETAILS_PATH} />,
      );
    }

    // Sale confirmed, release the carken... ykhm... other pages :)
    return routes.concat(
      SUMMARY_ROUTE,
      INCODE_SDK_ROUTE,
      !bankDetailsAdded ? ADD_BANK_DETAILS_ROUTE : [],
      !documentsAdded ? UPLOAD_DOCUMENTS_ROUTE : [],
      isDocCaptureCompleted || isAutomatedReviewStatusViewActive ? REVIEW_STATUS_ROUTE : [],

      <Redirect key="redirect" to={CONFIRM_SALE_SUMMARY_PATH} />,
    );
  };

  const resolveDocumentCaptureView = (
    <Switch>
      {resolveRoutes()}
    </Switch>
  );

  if (shouldOptOutOfLayout) {
    return resolveDocumentCaptureView;
  }

  return (
    <DocumentCaptureViewLayout>
      {resolveDocumentCaptureView}
    </DocumentCaptureViewLayout>
  );
};

export default DocumentCaptureView;
